import {
    CachedCaseListElement, CaseListSnapshot, DandelionService, EventsStorageInterface, parseElementWithZod, ZcaseListSnapshotsSchema,
    SnapshotAndEvents, SourceEventForList, ZcaseListSnapshots, ZodSchemas, ZsourceEvents, ZsourceEventsSchema, NamedBindings, LoggerInterface
} from "@dandelion/core";
import { inject, injectable } from "inversify";
import { NamedServices } from "../enum/NamedServices";
import { YearService } from "../services/YearService";

@injectable()
export class LocalStorageForEventsForProsafe extends DandelionService implements EventsStorageInterface {
    protected localStorage = window.localStorage;
    protected EventsForCentreCode: Map<string, SourceEventForList[]> = new Map();
    protected eventCounters: Map<string, number> = new Map();
    protected baseKey = 'events';
    protected baseSnapshotKey = 'snapshotEvents';

    constructor(@inject(NamedServices.YearService) protected yearService: YearService,
        @inject(NamedBindings.Logger) protected logger: LoggerInterface) {
        super();
    }

    async loadAllEvents(centreCode: string, caseType: string): Promise<SourceEventForList[]> {
        // const centreCodeWithyear = this.yearService.getCentreCodeWithSelectedYear().value;
        // const caseType = this.currentContextService.getCaseConfiguration().caseName;

        const storages = Object.keys(localStorage);
        const eventKey = `${this.baseKey}-${centreCode}-${caseType}`;
        const eventTypes = storages.filter((key) => key.startsWith(eventKey));
        let events: SourceEventForList[] = [];

        for (const key of eventTypes) {
            try {
                const raw = localStorage.getItem(key);
                if (raw) {
                    const parsedEvents = parseElementWithZod(ZsourceEventsSchema, ZodSchemas.SOURCE_EVENT, JSON.parse(raw));
                    events.push(...parsedEvents);
                }
            } catch (error) {
                this.logger.logWarning(`Errore nel parsing degli eventi da "${key}": ${error}`, {});
            }
        }
        return events.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    }

    async saveEvent(event: SourceEventForList, caseType: string): Promise<void> {
        const centreCode = this.yearService.getCentreCodeWithAdmissionYear().value;
        const eventKey = `${this.baseKey}-${centreCode}-${caseType}`;
        let events: ZsourceEvents = [];

        const loadedEvents = localStorage.getItem(eventKey);
        if (loadedEvents) {
            try {
                events = parseElementWithZod(ZsourceEventsSchema, ZodSchemas.SOURCE_EVENT, JSON.parse(loadedEvents));
            } catch (error) {
                console.warn('Errore nel parsing degli eventi da localStorage:', error);
            }
        }
        events.push(event);
        this.localStorage.setItem(eventKey, JSON.stringify(events));
    }

    protected getLoadedSnapshots(centreCode: string, caseType: string): ZcaseListSnapshots {
        let snapshots: ZcaseListSnapshots = [];
        const snapshotKey = `${this.baseSnapshotKey}-${centreCode}-${caseType}`;
        const loadedSnapshots = this.localStorage.getItem(snapshotKey);
        if (loadedSnapshots) {
            snapshots = parseElementWithZod(ZcaseListSnapshotsSchema, ZodSchemas.SNAPSHOTS, JSON.parse(loadedSnapshots));
        }
        return snapshots;
    }

    async getLastSnapshotAndEvents(caseType: string): Promise<SnapshotAndEvents> {
        const selectedYearCentreCode = this.yearService.getCentreCodeWithSelectedYear().value;
        const previousYearCentreCode = this.yearService.getCentreCodeWithPreviousYear().value;
        const selectedYear = this.yearService.getSelectedYear();
        const currentYear = new Date().getFullYear();

        const allCentreCodes = [selectedYearCentreCode];
        if (selectedYear === currentYear && previousYearCentreCode) {
            allCentreCodes.push(previousYearCentreCode);
        }

        let combinedEvents: SourceEventForList[] = [];
        let snapshotsArray: CaseListSnapshot[] = [];

        for (const code of allCentreCodes) {
            const events = await this.loadAllEvents(code, caseType);
            const existingEvents = this.EventsForCentreCode.get(code) || [];

            const combinedEventsMap = new Map<string, SourceEventForList>();
            [...existingEvents, ...events].forEach(event => {
                combinedEventsMap.set(event.id, event);
            });
            const uniqueEvents = Array.from(combinedEventsMap.values());
            this.EventsForCentreCode.set(code, uniqueEvents);
            combinedEvents.push(...uniqueEvents);

            const currentCount = this.eventCounters.get(code) || 0;
            if (events.length > currentCount) {
                this.eventCounters.set(code, events.length);
            }

            const snapshots: ZcaseListSnapshots = this.getLoadedSnapshots(code, caseType);
            snapshotsArray.push(...snapshots);
        }
        if (snapshotsArray.length === 0) {
            return {
                events: combinedEvents,
                snapshot: new CaseListSnapshot([], [], [], new Date(), 0),
            };
        }
        const latestSnapshot = snapshotsArray.reduce((prev, curr) => prev.date > curr.date ? prev : curr);
        const eventsAfterSnapshot = combinedEvents.filter(e => e.timestamp > latestSnapshot.date);
        return { events: eventsAfterSnapshot, snapshot: latestSnapshot };
    }

    async saveSnapshot(snapshot: CaseListSnapshot, caseType: string): Promise<void> {
        const centreCode = this.yearService.getCentreCodeWithAdmissionYear().value;
        // Estrai l’anno dal centreCode
        const year = parseInt(centreCode.split('_')[1], 10);
        if (isNaN(year)) {
            throw new Error('Invalid year in centreCode');
        }
        // Filtra le liste per l’anno selezionato
        const filterByYear = ({ referenceDate }: CachedCaseListElement) => new Date(referenceDate).getFullYear() === year;
        const filteredList = snapshot.cachedCaseList.filter(filterByYear);
        const filteredDeletedList = snapshot.deletedCachedCaseList.filter(filterByYear);;
        // Recupera eventi e contatori
        const eventsForCentreCode = this.EventsForCentreCode.get(centreCode) || [];
        const numberOfEvents = eventsForCentreCode.length;
        const eventCounter = this.eventCounters.get(centreCode) || 0;
        // Salvataggio solo se si è superata la soglia
        if (eventCounter <= 10) {
            return;
        }
        // Crea snapshot filtrato
        const filteredSnapshot = new CaseListSnapshot(filteredList, filteredDeletedList, snapshot.permanentlyDeletedCaseList, new Date(), numberOfEvents);
        this.eventCounters.set(centreCode, 0);
        // Salva in localStorage
        const snapshots: ZcaseListSnapshots = this.getLoadedSnapshots(centreCode, caseType);
        snapshots.push(filteredSnapshot);
        this.localStorage.setItem(`${this.baseSnapshotKey}-${centreCode}-${caseType}`, JSON.stringify(snapshots));
    }
}